import React, { useRef, useState } from 'react';
import moment from 'moment-timezone';
import { Popover, Input, Button, message } from 'antd';
import {
	BellFilled,
	BellOutlined,
	CloseOutlined,
	DeleteOutlined,
	StarFilled,
	StarOutlined,
} from '@ant-design/icons';
import {
	getReminderStatus,
	isWorkOrderRemindersEnabled,
	REMINDER_STATUS,
} from '../../data/workOrderStarLabels';
import useMountEffect from '../../hooks/useMountEffect';
import useExclusivePopover from '../../hooks/useExclusivePopover';
import useReminderNow from '../../hooks/useReminderNow';
import useReminderRinging from '../../hooks/useReminderRinging';
import ProUpsellContent from './ProUpsellContent';
import connectStarActions, { StarActionProps } from './connectStarActions';
import useStarActions, { StarSnapshot } from './useStarActions';
import ReminderDateTimeInput, { REMINDER_MINUTE_STEP } from './ReminderDateTimeInput';
import ReminderMenu from './ReminderMenu';
import OWButton from '../ow_button/OWButton';
import './StarNotePopover.less';

interface OwnProps {
	workOrderId: number | string;
	initialStarred?: boolean;
	initialNote?: string | null;
	initialRemindAt?: moment.Moment | null;
	// The saved "Remind me only if…" condition.
	initialCondition?: string | null;
	// The work order's due date and scheduled time, behind the "Past due" / "Past schedule" chips.
	dueDate?: string | number | null;
	scheduledAt?: string | number | null;
	noteDisplay?: React.ReactNode;
	children: React.ReactElement;
}

type Props = OwnProps & StarActionProps;

const NOTE_MAX_LENGTH = 40;
// antd uppercases button text app-wide; these buttons keep their own case.
const KEEP_CASE_CLASS = '[&.ant-btn]:!normal-case [&.ant-btn]:!tracking-normal';
const CHIP_CLASS = `footnoteregular ${KEEP_CASE_CLASS}`;

// One-click reminder times, counted from the moment the chip is pressed.
const QUICK_REMINDERS: {
	label: string;
	amount: number;
	unit: moment.unitOfTime.DurationConstructor;
}[] = [
	{ label: 'In an hour', amount: 1, unit: 'hour' },
	{ label: 'In a day', amount: 1, unit: 'day' },
	{ label: 'In 2 days', amount: 2, unit: 'days' },
	{ label: 'In a week', amount: 1, unit: 'week' },
	{ label: 'In 2 weeks', amount: 2, unit: 'weeks' },
	{ label: 'In a month', amount: 1, unit: 'month' },
];

// One-click conditions; free text is still allowed until conditions are designed with the backend.
const QUICK_CONDITIONS = ["Supplier doesn't respond", 'Work order not complete'];

const toReminderTime = (time: moment.Moment): moment.Moment =>
	time.clone().second(0).millisecond(0);

// A new reminder starts at the first quarter hour at least 15 minutes out, so the prefilled
// time is always one the picker allows and the backend accepts.
const defaultReminderTime = (): moment.Moment => {
	const earliest = moment().add(REMINDER_MINUTE_STEP, 'minutes');
	const roundedMinutes = Math.ceil(earliest.minute() / REMINDER_MINUTE_STEP) * REMINDER_MINUTE_STEP;
	return earliest.clone().minute(0).second(0).millisecond(0).add(roundedMinutes, 'minutes');
};

const isSameReminder = (first: moment.Moment | null, second: moment.Moment | null): boolean =>
	first === null || second === null ? first === second : first.valueOf() === second.valueOf();

// Scrolling anywhere closes the popover (it is anchored to a row that would scroll away), but
// the reminder picker's time columns scroll as part of using it.
const isScrollInsidePicker = (event: Event): boolean =>
	event.target instanceof Element && !!event.target.closest('.ant-picker-dropdown');

const StarNotePopover: React.FC<Props> = (props) => {
	const {
		workOrderId,
		initialStarred = false,
		initialNote = '',
		initialRemindAt = null,
		initialCondition = null,
		dueDate = null,
		scheduledAt = null,
		noteDisplay,
		canAddNote = true,
		children,
		readWorkOrderStar,
	} = props;
	// Saved state from props; the note and reminder only mean something while the WO is starred.
	const committedNote = initialStarred ? initialNote || null : null;
	const committedRemindAt = initialStarred ? initialRemindAt : null;
	const committedCondition = committedRemindAt ? initialCondition || null : null;
	const committed: StarSnapshot = {
		note: committedNote,
		remindAt: committedRemindAt,
		condition: committedCondition,
	};

	const now = useReminderNow(committedRemindAt);
	// With reminders off a saved reminder is hidden here (it still rides along on save, so it isn't
	// cleared) and the star never shows as a bell.
	const canRemind = canAddNote && isWorkOrderRemindersEnabled();
	const isRinging = useReminderRinging(workOrderId, committedRemindAt);
	const reminderStatus = isWorkOrderRemindersEnabled()
		? getReminderStatus(committedRemindAt, isRinging)
		: REMINDER_STATUS.NONE;

	const [open, setOpen] = useState(false);
	const [mode, setMode] = useState<'editor' | 'menu'>('editor');
	const [draftStarred, setDraftStarred] = useState(initialStarred);
	const [draftNote, setDraftNote] = useState(committedNote || '');
	const [draftRemindAt, setDraftRemindAt] = useState<moment.Moment | null>(committedRemindAt);
	const [showReminderRow, setShowReminderRow] = useState(!!committedRemindAt);
	// "Remind me only if…": saved with the reminder as `remindCondition`.
	const [draftCondition, setDraftCondition] = useState(committedCondition || '');
	// Collapsed to a "+ Remind me only if…" link unless a saved condition is being edited.
	const [showConditionRow, setShowConditionRow] = useState(!!committedCondition);
	const [openPicker, setOpenPicker] = useState(false);

	const { isPersisting, track, untrack, snooze, stop } = useStarActions(workOrderId, props);

	const openRef = useRef(open);
	openRef.current = open;

	// Only one floating popover (this, the label upsell, …) open at a time.
	useExclusivePopover(open, () => setOpen(false));

	useMountEffect(() => {
		const closeOnScroll = (event: Event) => {
			if (openRef.current && !isScrollInsidePicker(event)) setOpen(false);
		};
		window.addEventListener('scroll', closeOnScroll, true);
		return () => window.removeEventListener('scroll', closeOnScroll, true);
	});

	// Search rows don't carry a reminder's condition and the PUT replaces the whole star, so read the
	// star from the work order before editing or snoozing a saved reminder (the read updates the
	// cached row too). Resolves the saved condition; falls back to the props if the read fails.
	const readSavedCondition = (): Promise<string | null> =>
		committedRemindAt
			? readWorkOrderStar(workOrderId)
					.then((latest) =>
						latest === undefined ? committedCondition : (latest && latest.remindCondition) || null
					)
					.catch(() => committedCondition)
			: Promise.resolve(committedCondition);

	const openEditor = (options: {
		draftStarred: boolean;
		draftNote?: string;
		draftRemindAt?: moment.Moment | null;
		openPicker?: boolean;
		// A new time for the saved reminder (Custom snooze) keeps its condition.
		keepCondition?: boolean;
		// The saved reminder's condition as just read; the props catch up a render later.
		savedCondition?: string | null;
	}) => {
		const nextRemindAt =
			options.draftRemindAt !== undefined ? options.draftRemindAt : committedRemindAt;
		setDraftStarred(options.draftStarred);
		setDraftNote(options.draftNote !== undefined ? options.draftNote : committedNote || '');
		setDraftRemindAt(nextRemindAt);
		setShowReminderRow(!!nextRemindAt);
		// Editing the saved reminder (or moving it to a new time) starts from its saved condition,
		// shown open; a new reminder starts blank and collapsed.
		const keepsSavedReminder = options.draftRemindAt === undefined || !!options.keepCondition;
		const savedCondition =
			options.savedCondition !== undefined ? options.savedCondition : committedCondition;
		const nextCondition = keepsSavedReminder ? savedCondition || '' : '';
		setDraftCondition(nextCondition);
		setShowConditionRow(!!nextCondition);
		setOpenPicker(!!options.openPicker);
		setMode('editor');
		setOpen(true);
	};

	const handleStarClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		if (isPersisting) return;
		if (!initialStarred) {
			track(null, null, null);
			return;
		}
		if (!committedNote && !committedRemindAt) {
			// Starred with nothing attached: clicking the star unstars immediately (no
			// popover) and shows the untrack toast (with UNDO). The popover is only for
			// confirming when there is a note or reminder that would go with it.
			untrack(committed);
			return;
		}
		openEditor({ draftStarred: false });
	};

	const handleDropdownClick = (event: React.MouseEvent) => {
		event.stopPropagation();
		if (isPersisting) return;
		if (!initialStarred) {
			// Not starred yet: open ready to star + add a note.
			openEditor({ draftStarred: true, draftNote: '', draftRemindAt: null });
			return;
		}
		if (reminderStatus === REMINDER_STATUS.ACTIVE) {
			// A due reminder is answered (stop / snooze), not edited.
			setMode('menu');
			setOpen(true);
			return;
		}
		// Already starred: edit the note/reminder and KEEP it starred. Only the star circle
		// opens the untrack draft.
		readSavedCondition().then((savedCondition) =>
			openEditor({ draftStarred: true, savedCondition })
		);
	};

	// Opens the reminder section empty: the user picks a time or one of the quick chips.
	const handleAddReminder = () => {
		setDraftStarred(true);
		setDraftRemindAt(null);
		setShowReminderRow(true);
		setOpenPicker(false);
	};

	const handleRemoveReminder = () => {
		setDraftRemindAt(null);
		setDraftCondition('');
		setShowConditionRow(false);
		setShowReminderRow(false);
	};

	const handleRemoveCondition = () => {
		setDraftCondition('');
		setShowConditionRow(false);
	};

	const handleSave = () => {
		if (isPersisting) return;
		if (!draftStarred) {
			if (!initialStarred) {
				setOpen(false);
				return;
			}
			untrack(committed).then((ok) => ok && setOpen(false));
			return;
		}
		// "+ 🔔" opened the reminder row: saving without a time would quietly save a plain star (or, on a tracked
		// work order, close as if nothing changed). Checked before the no-change shortcut.
		if (showReminderRow && !draftRemindAt) {
			message.error('Choose a reminder time, or remove the reminder.');
			return;
		}
		// With reminders off a hidden saved reminder rides along, but one already due can't (the
		// backend refuses a past time), so it is dropped rather than failing the note edit.
		const nextRemindAt =
			isWorkOrderRemindersEnabled() || !draftRemindAt || draftRemindAt.isAfter(moment())
				? draftRemindAt
				: null;
		const nextNote = draftNote.trim() || null;
		const nextCondition = nextRemindAt ? draftCondition.trim() || null : null;
		if (
			initialStarred &&
			nextNote === committedNote &&
			isSameReminder(nextRemindAt, committedRemindAt) &&
			nextCondition === committedCondition
		) {
			setOpen(false);
			return;
		}
		if (nextRemindAt && !nextRemindAt.isAfter(moment())) {
			message.error('Choose a reminder time in the future.');
			return;
		}
		track(nextNote, nextRemindAt, initialStarred ? committed : null, nextCondition).then(
			(ok) => ok && setOpen(false)
		);
	};

	const runMenuAction = (action: Promise<boolean>) => {
		setOpen(false);
		return action;
	};

	// Chips that remind when the work order goes past one of its own dates — the same dates the
	// "Past due" / "Past schedule" list filters compare against. Disabled when the date is unset or
	// already behind us.
	const workOrderDateChips = [
		{ label: 'Past schedule', date: scheduledAt, missing: 'No scheduled time' },
		{ label: 'Past due', date: dueDate, missing: 'No due date' },
	].map((chip) => {
		const time = chip.date ? moment(chip.date) : null;
		// Checked after rounding to the minute: a date seconds away rounds into the past, which
		// saving would then reject.
		const reminderTime = time && time.isValid() ? toReminderTime(time) : null;
		const usable = !!reminderTime && reminderTime.isAfter(moment());
		return {
			label: chip.label,
			time: usable ? reminderTime : null,
			title: usable ? time.format('MMM D, YYYY h:mm A') : time ? 'Already passed' : chip.missing,
		};
	});

	const draftReminderActive = !!draftRemindAt && !draftRemindAt.isAfter(now);
	const hasDraftReminder = canRemind && showReminderRow && !!draftRemindAt;
	const AvatarIcon: any = hasDraftReminder
		? draftStarred
			? BellFilled
			: BellOutlined
		: draftStarred
		? StarFilled
		: StarOutlined;
	const avatarClass = !draftStarred
		? 'border-grey-5 bg-white'
		: hasDraftReminder
		? draftReminderActive
			? '!border-character-danger !bg-character-danger'
			: '!border-calendulaGold-8 !bg-calendulaGold-8'
		: '!border-calendulaGold-5 !bg-calendulaGold-5';

	const title = !draftStarred
		? committedRemindAt && canRemind
			? 'Untrack Work Order and Remove Reminder'
			: 'Untrack Work Order'
		: showReminderRow && canRemind
		? 'Track Work Order and set reminder'
		: 'Track Work Order with note';

	const showUpsell = !canAddNote && !committedNote && !committedRemindAt;

	const upsellContent = (
		<ProUpsellContent
			message="Pro users can track work orders with a note or reminder"
			onClose={() => setOpen(false)}
		/>
	);

	const menuContent = (
		<div
			onClick={(event) => event.stopPropagation()}
			onMouseDown={(event) => event.stopPropagation()}
		>
			<ReminderMenu
				onStop={() => runMenuAction(stop(committed))}
				canSnooze={canAddNote}
				onSnooze={(minutes) =>
					runMenuAction(
						readSavedCondition().then((condition) =>
							snooze(
								{ ...committed, condition },
								moment().add(minutes, 'minutes').second(0).millisecond(0)
							)
						)
					)
				}
				onCustom={() =>
					readSavedCondition().then((savedCondition) =>
						openEditor({
							draftStarred: true,
							draftRemindAt: defaultReminderTime(),
							openPicker: true,
							keepCondition: true,
							savedCondition,
						})
					)
				}
			/>
		</div>
	);

	const editorContent = (
		<div
			onClick={(event) => event.stopPropagation()}
			onMouseDown={(event) => event.stopPropagation()}
			className="relative w-[368px] pb-2xs pt-2xs"
		>
			<CloseOutlined
				onClick={() => setOpen(false)}
				className="absolute right-0 top-2xs cursor-pointer text-sm text-character-secondary"
			/>

			<div className="flex flex-col gap-m">
				<div className="flex flex-col gap-s">
					<div>
						{/* Wraps as whole phrases: a long title ("Untrack Work Order and Remove Reminder")
						    pushes "Only visible to you" onto its own line rather than squeezing it. */}
						<div className="flex flex-wrap items-baseline gap-x-3 pb-xs pr-[22px]">
							<span className="bodyregular whitespace-nowrap text-character-primary">{title}</span>
							<span className="bodyregular whitespace-nowrap text-character-secondary">
								Only visible to you
							</span>
						</div>

						<div className="flex items-start gap-xs">
							<span
								onClick={() => setDraftStarred((current) => !current)}
								title={draftStarred ? 'Untrack' : 'Track'}
								className={`box-border inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full border border-solid ${avatarClass}`}
							>
								<AvatarIcon
									className={`text-xl ${draftStarred ? '!text-white' : '!text-character-title-85'}`}
								/>
							</span>
							<Input
								value={draftNote}
								disabled={!canAddNote}
								onChange={(event) => setDraftNote(event.target.value.slice(0, NOTE_MAX_LENGTH))}
								maxLength={NOTE_MAX_LENGTH}
								suffix={
									draftStarred ? (
										<span className="footnoteregular text-character-secondary">
											{`${draftNote.length}/${NOTE_MAX_LENGTH}`}
										</span>
									) : null
								}
								placeholder="Add a note"
								className={
									!draftStarred && draftNote.length > 0
										? 'text-grey-7 line-through'
										: 'bodyregular text-character-primary'
								}
							/>
						</div>
					</div>

					{showReminderRow && canRemind ? (
						<>
							<div className="flex items-center gap-xs">
								<ReminderDateTimeInput
									value={draftRemindAt}
									onChange={setDraftRemindAt}
									struck={!draftStarred}
									defaultOpen={openPicker}
								/>
								<Button
									danger
									icon={<DeleteOutlined />}
									onClick={handleRemoveReminder}
									disabled={!draftStarred}
									title="Remove reminder"
								/>
							</div>
							<div className="flex flex-wrap gap-xs">
								{QUICK_REMINDERS.map((option) => (
									<Button
										key={option.label}
										shape="round"
										size="small"
										className={CHIP_CLASS}
										disabled={!draftStarred}
										onClick={() =>
											setDraftRemindAt(toReminderTime(moment().add(option.amount, option.unit)))
										}
									>
										{option.label}
									</Button>
								))}
								{workOrderDateChips.map((chip) => (
									<Button
										key={chip.label}
										shape="round"
										size="small"
										className={CHIP_CLASS}
										disabled={!draftStarred || !chip.time}
										title={chip.title}
										onClick={() => setDraftRemindAt(chip.time)}
									>
										{chip.label}
									</Button>
								))}
							</div>
							{showConditionRow ? (
								<div className="flex flex-col gap-xs pt-xs">
									<span className="bodyregular text-character-primary">Remind me only if…</span>
									<div className="flex items-center gap-xs">
										<Input
											value={draftCondition}
											onChange={(event) => setDraftCondition(event.target.value)}
											disabled={!draftStarred}
											placeholder="e.g. If supplier doesn't respond"
											className="bodyregular"
										/>
										<Button
											danger
											icon={<DeleteOutlined />}
											onClick={handleRemoveCondition}
											disabled={!draftStarred}
											title="Remove condition"
										/>
									</div>
									<div className="flex flex-wrap gap-xs">
										{QUICK_CONDITIONS.map((condition) => (
											<Button
												key={condition}
												shape="round"
												size="small"
												className={CHIP_CLASS}
												disabled={!draftStarred}
												onClick={() => setDraftCondition(condition)}
											>
												{condition}
											</Button>
										))}
									</div>
								</div>
							) : (
								<Button
									type="link"
									size="small"
									className={`bodyregular self-start !px-0 ${KEEP_CASE_CLASS}`}
									disabled={!draftStarred}
									onClick={() => setShowConditionRow(true)}
								>
									+ Remind me only if…
								</Button>
							)}
						</>
					) : null}
				</div>

				<div className="flex justify-end gap-xs">
					{!showReminderRow && canRemind ? (
						<Button onClick={handleAddReminder} disabled={isPersisting}>
							+ Remind Me
							<BellOutlined />
						</Button>
					) : null}
					<Button onClick={() => setOpen(false)} disabled={isPersisting}>
						CANCEL
					</Button>
					<OWButton type="primary" onClick={handleSave} loading={isPersisting}>
						{draftStarred ? 'SAVE' : 'UNTRACK'}
					</OWButton>
				</div>
			</div>
		</div>
	);

	const trigger = React.cloneElement(children, {
		starred: initialStarred,
		note: committedNote,
		noteDisplay,
		loading: isPersisting,
		reminderStatus,
		onStarClick: handleStarClick,
		onDropdownClick: handleDropdownClick,
		onClick: undefined,
	});

	const isMenu = mode === 'menu' && !showUpsell;

	return (
		<Popover
			content={showUpsell ? upsellContent : isMenu ? menuContent : editorContent}
			trigger={[]}
			visible={open}
			onVisibleChange={setOpen}
			// Unmount the editor when it closes: its date picker renders in a portal and would otherwise
			// stay open on the page (e.g. when a scroll closes the popover mid-pick).
			destroyTooltipOnHide
			overlayClassName={isMenu ? '[&_.ant-popover-inner-content]:!p-0' : '!w-[400px]'}
			placement="bottomLeft"
		>
			{trigger}
		</Popover>
	);
};

export default connectStarActions(StarNotePopover as any) as React.ComponentType<OwnProps>;
